import React, { useCallback, useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from 'formik';
import DatePicker from "react-datepicker";
import { Multiselect } from "multiselect-react-dropdown";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import LogoDropdown from "components/UI/LogoDropdown";
import "./Fundraising.css";
import "../../assets/styles/scrollbar.css";
import { formattedDate } from "common/util_function";

const multiStyle = {
  chips: { background: "gray-300" },
  searchBox: {
    border: "none",
    borderRadius: "0px",
    padding: "0.5rem 1rem",
  },
};

const FundraisingForm = ({ formData, config, onSubmit }) => {
  const [selectedProjectLogo, setSelectedProjectLogo] = useState();
  const { projectList, fundingRounds, investors } = config;
  console.log('formData', formData);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setSelectedProjectLogo(formData.project || null);
    }

    return () => {
      isMounted = false;
    };
  }, [formData]);

  const validationSchema = Yup.object({
    projectId: Yup.number().required("Project Id is mandatory"),
    categoryId: Yup.string().required("Event Type is mandatory"),
    totalRaise: Yup.number().required("Total Raise is mandatory"),
    leadInvestorIds: Yup.array().min(1, "At least one lead investor must be selected").required("Lead Investors are mandatory"),
    investorIds: Yup.array().min(1, "At least one investor must be selected").required("Investors are mandatory"),
    raiseDate: Yup.date().required("Raised Date is mandatory"),
  });

  // form dropdown
  const getDropdownOptions = useCallback((list, defaultName) => {
    const defaultOption = <option key="default" value="Select">{defaultName}</option>
    const options = list?.length ? list.map((item, index) => {
      return (
        <option key={index} value={item.id}>
          {item.name.trim()}
        </option>
      );
    }): [];
    return [defaultOption, ...options];
  }, [investors]);

  return (
    <Formik
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, setFieldTouched, isSubmitting, errors, resetForm }) => (
        <Form noValidate>
          <div className="px-8 py-4">
            <div className="flex flex-wrap items-center justify-between px-4 py-2">
              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Project<span className="ml-1 text-red-500">*</span>
                </label>
                <div className="w-full pr-4">
                  <LogoDropdown
                    list={projectList}
                    selectedProjectItem={selectedProjectLogo}
                    onLogo={(item) => {
                      setSelectedProjectLogo(item);
                      setFieldValue('projectId', item.id)
                    }}
                    name="projectId"
                    value={values.projectId}
                  />
                  <div className="text-red-500 text-sm">
                    <ErrorMessage name="projectId" />
                  </div>
                </div>
              </div>
              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Fund Round<span className="ml-1 text-red-500">*</span>
                </label>
                <div className="w-full">
                  <Field
                    as="select"
                    name="categoryId"
                    required
                    className="w-full p-3 border-2 cursor-pointer outline-none focus:bg-gray-300"
                    selected={values.categoryId}
                  >
                    {getDropdownOptions(fundingRounds, "--Select Category--")}
                  </Field>
                </div>
                <div className="text-red-500 text-sm">
                  <ErrorMessage name="categoryId" />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between px-4 py-2">
              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Lead Investors<span className="ml-1 text-red-500">*</span>
                </label>
                <div className="w-full pr-4">
                  <div className="max-h-100-px border-2 cursor-pointer outline-none bg-white">
                    <Multiselect
                      options={investors}
                      displayValue="name"
                      showCheckbox={true}
                      placeholder="Select Lead Investors"
                      name="leadInvestorIds"
                      closeOnSelect={false}
                      style={multiStyle}
                      selectedValues={values.leadInvestorIds}
                      hidePlaceholder={true}
                      onSelect={(items) => {
                        setFieldValue("leadInvestorIds", items);
                      }}
                      onRemove={(items) => {
                        setFieldValue("leadInvestorIds", items);
                      }}
                    />
                  </div>
                  <div className="text-red-500 text-sm">
                    <ErrorMessage name="leadInvestorIds" />
                  </div>
                </div>
              </div>

              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Investors<span className="ml-1 text-red-500">*</span>
                </label>
                <div className="w-full">
                  <div className="max-h-100-px border-2 cursor-pointer outline-none bg-white">
                    <Multiselect
                      options={investors}
                      displayValue="name"
                      showCheckbox={true}
                      placeholder="Select Investors"
                      name="investorIds"
                      closeOnSelect={false}
                      style={multiStyle}
                      selectedValues={values.investorIds}
                      hidePlaceholder={true}
                      onSelect={(items) => {
                        setFieldValue("investorIds", items);
                      }}
                      onRemove={(items) => {
                        setFieldValue("investorIds", items);
                      }}
                    />
                  </div>
                  <div className="text-red-500 text-sm">
                    <ErrorMessage name="investorIds" />
                  </div>
                </div>
              </div>
            </div>

            

            <div className="flex flex-wrap items-center justify-between px-4 py-2">
              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Total Raise <span className="ml-1 text-red-500">*</span>
                </label>
                <div className="w-full pr-4">
                  <Field
                    type="text"
                    name="totalRaise"
                    placeholder="Total Raise"
                    required
                    className={`w-full p-3 border-2 cursor-pointer outline-none focus:bg-gray-300 ${errors.totalRaise && setFieldTouched.totalRaise ? "focus:bg-red-500" : ''}`}
                  />
                  <div className="text-red-500 text-sm">
                    <ErrorMessage name="totalRaise" />
                  </div>
                </div>
              </div>

              <div className="w-1/2">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  <i className="text-gray-500 fa fa-calendar text-lg leading-lg mr-3" />
                  Raised Date<span className="ml-1 text-red-500">*</span>
                </label>

                <div className="w-full">
                  <DatePicker
                    name="raiseDate"
                    selected={
                      values.raiseDate && values.raiseDate !== "1970-01-01"
                        ? new Date(values.raiseDate)
                        : null
                    }
                    autoComplete="off"
                    isClearable
                    dateFormat="MM/dd/yyyy"
                    className="w-full p-3 border-2 cursor-pointer outline-none focus:bg-gray-300"
                    onChange={(date) => setFieldValue("raiseDate", date)}
                  />
                  <ErrorMessage name="raiseDate" className="text-red-500 text-sm" />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between px-4 py-2">
              <div className="w-full">
                <label className="block uppercase text-gray-700 text-xs font-bold mb-2">
                  Announcement Link
                </label>
                <div className="w-full">
                  <Field
                    type="text"
                    name="announcementLink"
                    placeholder="Announcement Link"
                    className="w-full p-3 border-2 cursor-pointer outline-none focus:bg-gray-300"
                  />
                  <ErrorMessage name="announcementLink" className="text-red-500 text-sm" />
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-2 border-t-2 flex items-center justify-end">
            <button type="reset" className="bg-gray-300 text-gray-100 focus:outline-none font-bold py-2 px-6 rounded mr-4" onClick={resetForm}>Clear</button>
            <button type="submit" className="bg-blue-800 text-white focus:outline-none font-bold py-2 px-4 rounded">{isSubmitting && <span className="spinner-border spinner-border-sm mr-1">wait..</span>} Submit </button>
          </div>
        </Form>
      )}
    </Formik >
  )
}
export default FundraisingForm;